import { motion } from "framer-motion";
import { Heart, CalendarPlus } from "lucide-react";
import { toFavoriteCardViewModel } from "./favoriteCardViewModel";
import type { SavedFavorite } from "@/hooks/useFavorites";
import { RecipeCard } from "@/components/recipe/RecipeCard";

interface FavoriteCardProps {
  favorite: SavedFavorite;
  onTap: () => void;
  onToggleFavorite: (e: React.MouseEvent) => void;
  index?: number;
  isPremium?: boolean;
  members: Array<{ id: string; name?: string; age_months?: number | null }>;
  onAddToPlan?: () => void;
}

export function FavoriteCard({ favorite, onTap, onToggleFavorite, index = 0, isPremium = false, members, onAddToPlan }: FavoriteCardProps) {
  const vm = toFavoriteCardViewModel(favorite.recipe);
  const r = favorite.recipe as { calories?: number | null; proteins?: number | null; fats?: number | null; carbs?: number | null };
  const nutrition =
    r.calories != null || r.proteins != null || r.fats != null || r.carbs != null
      ? { calories: r.calories ?? null, proteins: r.proteins ?? null, fats: r.fats ?? null, carbs: r.carbs ?? null }
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.25 }}
    >
      <RecipeCard
        variant="preview"
        previewPresentation="collection"
        header={{
          mealLabel: vm.mealTypeLabel,
          cookingTimeMinutes: vm.cookingTimeMinutes,
          title: vm.title,
        }}
        ingredients={vm.ingredientNames}
        showIngredientChips={false}
        showHint={false}
        hint={vm.hint}
        nutrition={nutrition}
        nutritionGoals={(favorite.recipe as { nutrition_goals?: string[] | null }).nutrition_goals ?? []}
        nutritionGoalsMaxVisible={2}
        onClick={onTap}
        actions={
          <div className="flex flex-col items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(e);
              }}
              className="h-9 w-9 rounded-xl flex items-center justify-center text-primary/90 bg-muted/30 border border-border/50 hover:bg-muted/55 active:scale-[0.98] transition-all shrink-0"
              aria-label="Убрать из избранного"
            >
              <Heart
                className="h-[17px] w-[17px] fill-current text-primary"
                strokeWidth={0}
                aria-hidden
              />
            </button>
            {onAddToPlan && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToPlan();
                }}
                className="h-9 w-9 rounded-xl flex items-center justify-center text-muted-foreground/85 bg-transparent border border-border/45 hover:bg-muted/40 active:scale-[0.98] transition-all shrink-0"
                aria-label="Добавить в план"
              >
                <CalendarPlus className="h-[17px] w-[17px] stroke-[1.5]" />
              </button>
            )}
          </div>
        }
      />
    </motion.div>
  );
}
